// Route: /favorites — избранные материалы и коллекции. Быстрый доступ к
// помеченным звёздочкой сущностям. Источник — PowerSync (реактивно),
// фильтр favorite=true.

import { Button } from "@studyhub/ui/components/button";
import { EmptyState } from "@studyhub/ui/components/empty-state";
import { SectionHeader } from "@studyhub/ui/components/studyhub/section-header";
import { IconLibrary, IconStarFilled } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMaterialSets, useMaterialsList } from "@/components/materials/_api";
import { MaterialCardGrid } from "@/components/materials/material-card-grid";
import { SetCard } from "@/components/materials/set-card";
import {
	LIST_PAGE_GRID,
	LIST_PAGE_SHELL,
	LIST_PAGE_STACK,
	ListToolbar,
} from "@/components/shared/entity-list";
import { useMaterialsProgress } from "@/hooks/use-materials-progress";
import { useViewMode } from "@/hooks/use-view-mode";

const TILTS = [-1.2, 0.8, -0.6, 1, -0.9, 0.6] as const;

export const Route = createFileRoute("/favorites")({
	component: FavoritesPage,
});

function FavoritesPage(): React.ReactElement {
	const { t } = useTranslation("materials");
	const navigate = useNavigate();
	const [view, setView] = useViewMode("favorites", "grid");

	const materialsQuery = useMaterialsList();
	const setsQuery = useMaterialSets();
	const allSets = useMemo(() => setsQuery.data?.items ?? [], [setsQuery.data]);
	const favoriteSets = useMemo(() => allSets.filter((s) => s.favorite === true), [allSets]);
	const favoriteMaterials = useMemo(
		() => (materialsQuery.data?.items ?? []).filter((m) => m.favorite === true),
		[materialsQuery.data],
	);
	const totalCount = favoriteSets.length + favoriteMaterials.length;

	// Оба запроса уже прошли через shim — их `isLoading` честный и ограничен
	// потолком ожидания. Гасить его ещё раз флагом первичного синка нельзя:
	// после синка флаг всегда true, и «Избранного нет» показывалось на долю
	// секунды при каждом заходе (см. `use-powersync-ready.ts`).
	const isLoading = materialsQuery.isLoading || setsQuery.isLoading;
	const isError = materialsQuery.isError || setsQuery.isError;
	const isEmpty = !isLoading && !isError && totalCount === 0;
	const materialsProgress = useMaterialsProgress();

	const containerClass = view === "grid" ? LIST_PAGE_GRID : LIST_PAGE_STACK;

	return (
		<div className={LIST_PAGE_SHELL}>
			<SectionHeader
				icon={<IconStarFilled />}
				category="cards"
				title={t("favorites.title")}
				description={
					totalCount === 0
						? t("favorites.subtitleEmpty")
						: t("favorites.subtitle", {
								materials: t("list.count", { count: favoriteMaterials.length }),
								sets: t("list.setsCount", { count: favoriteSets.length }),
							})
				}
			/>

			{isEmpty ? (
				<EmptyState
					icon={<IconStarFilled />}
					title={t("favorites.empty.title")}
					description={t("favorites.empty.description")}
					action={
						<Button
							variant="outline"
							className="cursor-pointer gap-1.5"
							onClick={() => void navigate({ to: "/materials" })}
						>
							<IconLibrary size={16} stroke={1.75} />
							{t("favorites.empty.cta")}
						</Button>
					}
				/>
			) : (
				<div className="flex flex-col gap-6">
					<ListToolbar view={view} onViewChange={setView} />
					{favoriteSets.length > 0 ? (
						<div className={containerClass}>
							{favoriteSets.map((set, i) => (
								<SetCard key={set.id} set={set} view={view} tilt={TILTS[i % TILTS.length]} />
							))}
						</div>
					) : null}
					{favoriteMaterials.length > 0 || isLoading ? (
						<MaterialCardGrid
							materials={favoriteMaterials}
							sets={allSets}
							view={view}
							isLoading={isLoading && favoriteMaterials.length === 0}
							isError={isError && favoriteMaterials.length === 0}
							onRetry={() => void materialsQuery.refetch()}
							onUpload={() => void navigate({ to: "/materials" })}
							hasFilters={false}
							onResetFilters={() => void navigate({ to: "/materials" })}
							progressById={materialsProgress.progressById}
						/>
					) : null}
				</div>
			)}
		</div>
	);
}
